/** RGB offset for lift/gamma/gain style color wheels (shadows / midtones / highlights). */
export interface ColorWheel {
  r: number;
  g: number;
  b: number;
  master: number;
}

export interface HslQualifier {
  enabled: boolean;
  hueMin: number;
  hueMax: number;
  satMin: number;
  satMax: number;
  lumMin: number;
  lumMax: number;
  soft: number;
  /** Softness on low / high edges (0..1), Resolve-style. */
  hueSoft: number;
  satSoftLow: number;
  satSoftHigh: number;
  lumSoftLow: number;
  lumSoftHigh: number;
  invert: boolean;
  /** Qualifier pick color (hex) used as seed for HSL range. */
  pickHex: string;
  /** Matte finesse */
  blurRadius: number;
  cleanBlack: number;
  cleanWhite: number;
}

export interface HdrWheels {
  dark: ColorWheel;
  shadow: ColorWheel;
  light: ColorWheel;
  global: ColorWheel;
  darkExp: number;
  shadowExp: number;
  lightExp: number;
  globalExp: number;
  darkSat: number;
  shadowSat: number;
  lightSat: number;
  globalSat: number;
}

export interface CameraRawSettings {
  decodeQuality: string;
  decodeUsing: string;
  colorScience: string;
  whiteBalance: string;
  colorSpace: string;
  gamma: string;
  iso: number;
  highlightRecovery: boolean;
  colorTemp: number;
  tint: number;
  exposure: number;
  saturation: number;
  contrast: number;
  midpoint: number;
  highlightRolloff: number;
  shadowRolloff: number;
  whiteLevel: number;
  blackLevel: number;
  useVideoBlackLevel: boolean;
}

export interface ColorSpaceSettings {
  colorScience: string;
  processingMode: string;
  inputColorSpace: string;
  timelineColorSpace: string;
  timelineLuminance: string;
  outputColorSpace: string;
  limitOutputGamut: string;
  inputDrt: string;
  outputDrt: string;
  applyResizeIn: string;
  automaticColorManagement: boolean;
  separateColorSpaceGamma: boolean;
  inverseDrtSdrHdr: boolean;
  whitePointAdaptation: boolean;
  colorSpaceAwareTools: boolean;
  disableToneMappingFusion: boolean;
  graphicsWhiteLevel: number;
}

export interface ColorGrade {
  /** Lift / Gamma / Gain / Offset style wheels */
  lift: ColorWheel;
  gamma: ColorWheel;
  gain: ColorWheel;
  offset: ColorWheel;
  /** Tonal controls */
  exposure: number;
  contrast: number;
  pivots: number;
  shadows: number;
  midtones: number;
  highlights: number;
  whites: number;
  blacks: number;
  midDetail: number;
  colorBoost: number;
  lumMix: number;
  /** Color */
  saturation: number;
  hue: number;
  temperature: number;
  tint: number;
  intensity: number;
  /** Secondary / qualifier */
  qualifier: HslQualifier;
  /** HDR zone wheels */
  hdr: HdrWheels;
  /** Camera Raw panel (clip metadata + grade seeds) */
  cameraRaw: CameraRawSettings;
  /** Color management / transforms */
  colorSpace: ColorSpaceSettings;
}

export function defaultColorWheel(): ColorWheel {
  return { r: 0, g: 0, b: 0, master: 0 };
}

export function defaultQualifier(): HslQualifier {
  return {
    enabled: false,
    hueMin: 0,
    hueMax: 360,
    satMin: 0,
    satMax: 1,
    lumMin: 0,
    lumMax: 1,
    soft: 0.1,
    hueSoft: 0.05,
    satSoftLow: 0.05,
    satSoftHigh: 0.05,
    lumSoftLow: 0.05,
    lumSoftHigh: 0.05,
    invert: false,
    pickHex: '#ffffff',
    blurRadius: 0,
    cleanBlack: 0,
    cleanWhite: 0,
  };
}

export function defaultHdrWheels(): HdrWheels {
  return {
    dark: defaultColorWheel(),
    shadow: defaultColorWheel(),
    light: defaultColorWheel(),
    global: defaultColorWheel(),
    darkExp: 0,
    shadowExp: 0,
    lightExp: 0,
    globalExp: 0,
    darkSat: 1,
    shadowSat: 1,
    lightSat: 1,
    globalSat: 1,
  };
}

export function defaultCameraRaw(): CameraRawSettings {
  return {
    decodeQuality: 'Use Project Setting',
    decodeUsing: 'Clip',
    colorScience: 'Gen 5',
    whiteBalance: 'As Shot',
    colorSpace: 'Blackmagic Design',
    gamma: 'Blackmagic Design Custom',
    iso: 800,
    highlightRecovery: true,
    colorTemp: 5600,
    tint: 10,
    exposure: 0,
    saturation: 1,
    contrast: 1,
    midpoint: 0.41,
    highlightRolloff: 1,
    shadowRolloff: 1,
    whiteLevel: 1,
    blackLevel: 0,
    useVideoBlackLevel: true,
  };
}

export function defaultColorSpaceSettings(): ColorSpaceSettings {
  return {
    colorScience: 'DaVinci YRGB Color Managed',
    processingMode: 'Custom',
    inputColorSpace: 'Blackmagic Design Film Gen 5',
    timelineColorSpace: 'DaVinci WG/Intermediate',
    timelineLuminance: 'HDR 1000',
    outputColorSpace: 'Rec.709 (Scene)',
    limitOutputGamut: 'Output color space',
    inputDrt: 'DaVinci',
    outputDrt: 'DaVinci',
    applyResizeIn: 'Gamma',
    automaticColorManagement: false,
    separateColorSpaceGamma: false,
    inverseDrtSdrHdr: true,
    whitePointAdaptation: true,
    colorSpaceAwareTools: true,
    disableToneMappingFusion: false,
    graphicsWhiteLevel: 100,
  };
}

export function defaultColorGrade(): ColorGrade {
  return {
    lift: defaultColorWheel(),
    gamma: defaultColorWheel(),
    gain: defaultColorWheel(),
    offset: defaultColorWheel(),
    exposure: 0,
    contrast: 1,
    pivots: 0.5,
    shadows: 0,
    midtones: 0,
    highlights: 0,
    whites: 0,
    blacks: 0,
    midDetail: 0,
    colorBoost: 0,
    lumMix: 1,
    saturation: 1,
    hue: 0,
    temperature: 0,
    tint: 0,
    intensity: 1,
    qualifier: defaultQualifier(),
    hdr: defaultHdrWheels(),
    cameraRaw: defaultCameraRaw(),
    colorSpace: defaultColorSpaceSettings(),
  };
}

/** Normalize older projects missing new color fields. */
export function resolveColorGrade(raw: Partial<ColorGrade> | undefined | null): ColorGrade {
  const d = defaultColorGrade();
  if (!raw) return d;
  return {
    ...d,
    ...raw,
    lift: { ...d.lift, ...(raw.lift ?? {}) },
    gamma: { ...d.gamma, ...(raw.gamma ?? {}) },
    gain: { ...d.gain, ...(raw.gain ?? {}) },
    offset: { ...d.offset, ...(raw.offset ?? {}) },
    qualifier: { ...d.qualifier, ...(raw.qualifier ?? {}) },
    hdr: {
      ...d.hdr,
      ...(raw.hdr ?? {}),
      dark: { ...d.hdr.dark, ...(raw.hdr?.dark ?? {}) },
      shadow: { ...d.hdr.shadow, ...(raw.hdr?.shadow ?? {}) },
      light: { ...d.hdr.light, ...(raw.hdr?.light ?? {}) },
      global: { ...d.hdr.global, ...(raw.hdr?.global ?? {}) },
    },
    cameraRaw: { ...d.cameraRaw, ...(raw.cameraRaw ?? {}) },
    colorSpace: { ...d.colorSpace, ...(raw.colorSpace ?? {}) },
  };
}

function addWheel(a: ColorWheel, b: ColorWheel, scale = 1): ColorWheel {
  return {
    r: a.r + b.r * scale,
    g: a.g + b.g * scale,
    b: a.b + b.b * scale,
    master: a.master + b.master * scale,
  };
}

/**
 * Bake HDR zone wheels into the primary grade the GPU shader understands.
 * Camera Raw / Color Space metadata stay on the clip; Raw sliders also write primaries directly.
 */
export function bakeColorGradeForEngine(
  raw: Partial<ColorGrade> | undefined | null
): ColorGrade {
  const g = resolveColorGrade(raw);
  const h = g.hdr;

  return {
    ...g,
    lift: addWheel(addWheel(g.lift, h.dark, 0.7), h.shadow, 0.4),
    gain: addWheel(g.gain, h.light, 0.7),
    offset: addWheel(g.offset, h.global, 0.55),
    exposure:
      g.exposure +
      h.darkExp * 0.2 +
      h.shadowExp * 0.3 +
      h.lightExp * 0.25 +
      h.globalExp,
    saturation:
      g.saturation * ((h.darkSat + h.shadowSat + h.lightSat + h.globalSat) / 4),
    shadows: g.shadows + h.darkExp * 0.12,
    highlights: g.highlights + h.lightExp * 0.12,
  };
}
